import { get } from '@rover/react-lib/src/apis/serviceTypes.api';
import fetchServiceTypesResponseFactory from '@rover/react-lib/src/factories/fetchServiceTypesResponseFactory';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import generateActions from '@rover/react-lib/src/redux/ducks/generateActions';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import type { FetchServiceTypesResponse, SelectOption, ServiceType } from '@rover/types';
import { PetType } from '@rover/types/src/Pet';
import type { ServiceTypeSlug } from '@rover/types/src/ServiceType';

export type State = {
  errorMessage: string;
  loading: boolean;
  fetchServiceTypesResponse: FetchServiceTypesResponse;
};

type Actions = {
  fetchServiceTypesPending: ActionCreator<void>;
  fetchServiceTypesSuccess: ActionCreator<FetchServiceTypesResponse>;
  fetchServiceTypesFailure: ActionCreator<string>;
};

type Effects = {
  fetchServiceTypes: EffectCreator<void>;
};

type Selectors = {
  getServiceTypes: SelectorCreator<ServiceType[]>;
  getServiceTypeBySlug: SelectorCreator<(arg0: string) => ServiceType | null | undefined>;
  getPetTypeServiceTypes: SelectorCreator<(arg0: PetType[]) => ServiceType[]>;
  getName: SelectorCreator<(arg0: string) => string>;
  getDisplayName: SelectorCreator<(arg0: string) => string>;
  getPriceUnit: SelectorCreator<(arg0: string) => string>;
  getBrowsableServiceTypeOptions: SelectorCreator<SelectOption[]>;
  getBrowsableServiceTypes: SelectorCreator<ServiceType[]>;
  getPetTypeBrowsableServiceTypes: SelectorCreator<(arg0: PetType[]) => ServiceType[]>;
  getRepeatServiceTypes: SelectorCreator<ServiceType[]>;
  getServiceTypeActionBySlug: SelectorCreator<(arg0: ServiceTypeSlug) => string | null | undefined>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'serviceTypes',
  initialState: {
    errorMessage: '',
    loading: true,
    fetchServiceTypesResponse: fetchServiceTypesResponseFactory(),
  },
  actions: { ...generateActions('fetchServiceTypes') },
  effects: {
    fetchServiceTypes:
      () =>
      async (
        dispatch,
        getState,
        { fetchServiceTypesPending, fetchServiceTypesSuccess, fetchServiceTypesFailure }
      ) => {
        dispatch(fetchServiceTypesPending());

        try {
          const fetchServiceTypesResponse: FetchServiceTypesResponse = await get();
          dispatch(fetchServiceTypesSuccess(fetchServiceTypesResponse));
        } catch (e) {
          dispatch(fetchServiceTypesFailure((e as Error).message));
          captureError(e as Error);
        }
      },
  },
  selectors: {
    getServiceTypes: (getState, createSelector) =>
      createSelector([getState], (x) => x.fetchServiceTypesResponse.results),
    getServiceTypeBySlug: (getState, createSelector, { getServiceTypes }) =>
      createSelector(
        [getServiceTypes],
        (serviceTypes) => (serviceTypeSlug) => serviceTypes.find((s) => s.slug === serviceTypeSlug)
      ),
    getPetTypeServiceTypes: (getState, createSelector, { getServiceTypes }) =>
      createSelector(
        [getServiceTypes],
        (serviceTypes) => (petType) =>
          petType && petType.length
            ? serviceTypes.filter(
                (s) =>
                  s.suggestedPetType === 'any' || petType.includes(s.suggestedPetType as PetType)
              )
            : serviceTypes
      ),
    getName: (getState, createSelector, { getServiceTypeBySlug }) =>
      createSelector([getServiceTypeBySlug], (serviceTypeBySlug) => (serviceTypeSlug) => {
        const serviceType = serviceTypeBySlug(serviceTypeSlug);
        return (serviceType && serviceType.name) || '';
      }),
    getDisplayName: (getState, createSelector, { getServiceTypeBySlug }) =>
      createSelector([getServiceTypeBySlug], (serviceTypeBySlug) => (serviceTypeSlug) => {
        const serviceType = serviceTypeBySlug(serviceTypeSlug);
        return (serviceType && serviceType.displayName) || '';
      }),
    getPriceUnit: (getState, createSelector, { getServiceTypeBySlug }) =>
      createSelector([getServiceTypeBySlug], (serviceTypeBySlug) => (serviceTypeSlug) => {
        const serviceType = serviceTypeBySlug(serviceTypeSlug);
        return (serviceType && serviceType.alternativeRateUnitTranslated) || '';
      }),
    getBrowsableServiceTypeOptions: (getState, createSelector, { getServiceTypes }) =>
      createSelector([getServiceTypes], (x) =>
        x
          .filter((s) => s.browsable)
          .map((s) => ({
            title: s.name,
            subtitle: s.description,
            value: s.slug,
          }))
      ),
    getBrowsableServiceTypes: (getState, createSelector, { getServiceTypes }) =>
      createSelector([getServiceTypes], (serviceTypes) => serviceTypes.filter((s) => s.browsable)),
    getPetTypeBrowsableServiceTypes: (getState, createSelector, { getBrowsableServiceTypes }) =>
      createSelector(
        [getBrowsableServiceTypes],
        (browsableServices) => (petType) =>
          petType && petType.length
            ? browsableServices.filter(
                (s) =>
                  s.suggestedPetType === 'any' || petType.includes(s.suggestedPetType as PetType)
              )
            : browsableServices
      ),
    getRepeatServiceTypes: (getState, createSelector, { getServiceTypes }) =>
      createSelector([getServiceTypes], (serviceTypes) =>
        serviceTypes.filter((s) => s.supportsRepeatBookings)
      ),
    getServiceTypeActionBySlug: (getState, createSelector, { getServiceTypeBySlug }) =>
      createSelector(
        [getServiceTypeBySlug],
        (serviceTypeBySlug) => (serviceTypeSlug: ServiceTypeSlug) => {
          const serviceType = serviceTypeBySlug(serviceTypeSlug);
          return serviceType ? serviceType.action : null;
        }
      ),
  },
};

export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
